import { useState } from "react";
import { ICON_BASE_PATH } from "../../../utils/config";

interface InfoTooltipProps {
  /** Tooltip text content */
  tooltip: string;
  /** Subtitle text for callout variant */
  subtitle?: string;
  /** Direction: "up" (default) | "down" | "left" | "right" */
  direction?: "up" | "down" | "left" | "right";
  /** Variant: "default" (icon + text) | "callout" (box with title + subtitle) */
  variant?: "default" | "callout";
  /** Color theme: "dark" (default) | "light" */
  colorTheme?: "dark" | "light";
  /** Custom CSS class for the wrapper */
  className?: string;
  /** Custom CSS class for the tooltip */
  tooltipClassName?: string;
  /** Delay before showing tooltip in milliseconds */
  delay?: number;
  /** Custom icon source URL (only for default variant) */
  icon?: string;
  /** Icon alt text */
  iconAlt?: string;
  /** Max width of tooltip in pixels */
  maxWidth?: number;
  /** Whether to show on focus (for accessibility) */
  showOnFocus?: boolean;
  /** Callback when tooltip visibility changes */
  onVisibilityChange?: (visible: boolean) => void;
}

const InfoTooltip: React.FC<InfoTooltipProps> = ({
  tooltip,
  subtitle,
  direction = "up",
  variant = "default",
  colorTheme = "dark",
  className = "",
  tooltipClassName = "",
  delay = 0,
  icon = `${ICON_BASE_PATH}infoS.svg`,
  iconAlt = "Info",
  maxWidth = 240,
  showOnFocus = true,
  onVisibilityChange,
}) => {
  console.log(tooltip, subtitle);
  // const showTooltip = true;
  const [showTooltip, setShowTooltip] = useState(false);
  const [delayTimer, setDelayTimer] = useState<NodeJS.Timeout | null>(null);

  if (!tooltip) return null;

  const handleMouseEnter = () => {
    if (delay > 0) {
      const timer = setTimeout(() => setShowTooltip(true), delay);
      setDelayTimer(timer);
    } else {
      setShowTooltip(true);
      onVisibilityChange?.(true);
    }
  };

  const handleMouseLeave = () => {
    if (delayTimer) clearTimeout(delayTimer);
    setShowTooltip(false);
    onVisibilityChange?.(false);
  };

  const handleFocus = () => {
    if (showOnFocus) handleMouseEnter();
  };

  const handleBlur = () => {
    if (showOnFocus) handleMouseLeave();
  };

  const directionClass = {
    up: "gx_tooltip_up",
    down: "gx_tooltip_down",
    left: "gx_tooltip_left",
    right: "gx_tooltip_right",
  }[direction];

  const variantClass = variant === "callout" ? "gx_tooltip_callout" : "";
  const colorClass =
    variant === "callout" && colorTheme === "light" ? "gx_tooltip_light" : "";

  const isCallout = variant === "callout";

  if (isCallout) {
    return (
      <div
        className={`gx_callout_wrapper ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        role="tooltip"
        tabIndex={showOnFocus ? 0 : -1}
      >
        <div className={`gx_callout_container gx_callout_${direction}`}>
          <img src={icon} alt={iconAlt} className="gx_callout_icon" />
          {showTooltip && (
            <div
              className={`gx-uni-tooltip ${directionClass} ${variantClass} ${colorClass} ${tooltipClassName}`}
              style={{ maxWidth: `${maxWidth}px` }}
            >
              <div className="gx_tooltip_content">
                <h3 className="gx_tooltip_title">{tooltip}</h3>
                {subtitle && <p className="gx_tooltip_subtitle">{subtitle}</p>}
              </div>
              <div className="tooltip-arrow" />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`gx_info_icon_wrapper ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      role="tooltip"
      tabIndex={showOnFocus ? 0 : -1}
    >
      <img src={icon} alt={iconAlt} />

      {showTooltip && (
        <div
          className={`gx-uni-tooltip ${directionClass} ${colorClass} ${tooltipClassName}`}
          style={{ maxWidth: `${maxWidth}px` }}
        >
          <div className="gx_tooltip_content">
            <h3 className="gx_tooltip_title gx-p12-b">{tooltip}</h3>
            {subtitle && <p className="gx_tooltip_subtitle gx-p12-sb">{subtitle}</p>}
          </div>
          <div className="tooltip-arrow" />
        </div>
      )}
    </div>
  );
};

export default InfoTooltip;
